import React, { useState, useEffect, useContext } from 'react';
import {
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  CalendarIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowPathIcon,
  FlagIcon,
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  LockOpenIcon,
  BookOpenIcon,
  ScaleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';

interface Member {
  id: number;
  name: string;
  vorname: string;
  email: string;
  is_auditor: number;
}

interface Assignment {
  id: number;
  member_id: number;
  audit_year: number;
  access_start: string;
  access_end: string;
  notes: string | null;
  status: 'active' | 'completed';
  completed_at: string | null;
  created_at: string;
  name: string;
  vorname: string;
  email: string;
  assigned_by_name: string;
  assigned_by_vorname: string;
}

interface GroupedAssignments {
  member_id: number;
  name: string;
  vorname: string;
  email: string;
  assignments: Assignment[];
}

interface ProgressStats {
  total_bookings: number;
  total_income: number;
  total_expenses: number;
  balance: number;
  checked: number;
  flagged: number;
  open: number;
  progress_percent: number;
}

interface FlaggedBooking {
  id: number;
  buchungstag: string;
  betrag: number;
  verwendungszweck: string;
  name_zahlungsbeteiligter: string;
  status: string;
  note: string;
  checked_at: string;
  checker_name: string;
  checker_vorname: string;
}

interface AuditorProgress {
  id: number;
  member_id: number;
  status: 'active' | 'completed';
  completed_at: string | null;
  name: string;
  vorname: string;
  email: string;
}

const AdminAuditors: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<Member[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'auditors' | 'progress' | 'info'>('auditors');

  // Progress state
  const [progressYear, setProgressYear] = useState(new Date().getFullYear());
  const [progressStats, setProgressStats] = useState<ProgressStats | null>(null);
  const [flaggedBookings, setFlaggedBookings] = useState<FlaggedBooking[]>([]);
  const [auditorProgress, setAuditorProgress] = useState<AuditorProgress[]>([]);
  const [progressLoading, setProgressLoading] = useState(false);

  // Form state
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [accessStart, setAccessStart] = useState('');
  const [accessEnd, setAccessEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

  const isBoardMember = user?.roles?.some((r: any) => r.name === 'board');

  useEffect(() => {
    if (isBoardMember) fetchData();
  }, [isBoardMember]);

  useEffect(() => {
    if (activeTab === 'progress' && isBoardMember) fetchProgress();
  }, [activeTab, progressYear, isBoardMember]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, assignmentsRes] = await Promise.all([
        apiClient.get('/api/audit/admin/members'),
        apiClient.get('/api/audit/admin/assignments'),
      ]);

      if (membersRes.data.success) {
        const sortedMembers = [...membersRes.data.members].sort((a, b) => {
          const nameA = `${a.nachname} ${a.vorname}`.toLowerCase();
          const nameB = `${b.nachname} ${b.vorname}`.toLowerCase();
          return nameA.localeCompare(nameB, 'de');
        });
        setMembers(sortedMembers);
      }
      if (assignmentsRes.data.success) setAssignments(assignmentsRes.data.assignments);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    setProgressLoading(true);
    try {
      const response = await apiClient.get(`/api/audit/admin/progress/${progressYear}`);
      if (response.data.success) {
        setProgressStats(response.data.stats);
        setFlaggedBookings(response.data.flagged_with_notes || []);
        setAuditorProgress(response.data.auditors || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Fortschritts:', err);
    } finally {
      setProgressLoading(false);
    }
  };

  const groupedAssignments: GroupedAssignments[] = React.useMemo(() => {
    const grouped = new Map<number, GroupedAssignments>();
    assignments.forEach((a) => {
      if (!grouped.has(a.member_id)) {
        grouped.set(a.member_id, {
          member_id: a.member_id,
          name: a.name,
          vorname: a.vorname,
          email: a.email,
          assignments: [],
        });
      }
      grouped.get(a.member_id)!.assignments.push(a);
    });
    return Array.from(grouped.values());
  }, [assignments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember || selectedYears.length === 0 || !accessStart || !accessEnd) return;
    setSubmitting(true);
    try {
      const response = await apiClient.post('/api/audit/admin/assignments', {
        member_id: selectedMember,
        audit_years: selectedYears,
        access_start: accessStart,
        access_end: accessEnd,
        notes: notes || null,
      });
      if (response.data.success) {
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAssignment = async (id: number) => {
    try {
      const response = await apiClient.delete(`/api/audit/admin/assignments/${id}`);
      if (response.data.success) {
        fetchData();
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const handleRemoveAuditor = async (memberId: number) => {
    try {
      const response = await apiClient.delete(`/api/audit/admin/auditor/${memberId}`);
      if (response.data.success) fetchData();
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
    }
  };

  const handleReopen = async (assignmentId: number) => {
    try {
      const response = await apiClient.post(`/api/audit/admin/reopen/${assignmentId}`);
      if (response.data.success) {
        fetchData();
        fetchProgress();
      }
    } catch (err) {
      console.error('Fehler beim Wiedereröffnen:', err);
    }
  };

  const handleDownloadPDF = async (year: number, memberId: number, auditorName: string) => {
    try {
      const response = await apiClient.get(`/api/audit/protocol-pdf/${year}/${memberId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kassenpruefungsprotokoll_${year}_${auditorName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Fehler beim PDF-Download:', err);
    }
  };

  const resetForm = () => {
    setSelectedMember(null);
    setSelectedYears([]);
    setAccessStart('');
    setAccessEnd('');
    setNotes('');
  };

  const toggleYear = (year: number) => {
    setSelectedYears((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('de-DE');
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';
  const subTileClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const inputClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const primaryBtn = 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed';
  const secondaryBtn = 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 font-semibold rounded-xl transition';

  if (!isBoardMember) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'transparent' }}>
        <div className={`rounded-3xl p-8 max-w-md text-center ${cardClass}`}>
          <ShieldCheckIcon className="h-16 w-16 text-rose-600 dark:text-rose-400 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Zugriff verweigert</h2>
          <p className="text-gray-600 dark:text-slate-400">Diese Seite ist nur für Vorstandsmitglieder zugänglich.</p>
        </div>
      </div>
    );
  }

  const tabBtn = (active: boolean) =>
    `inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
      active ? 'bg-amber-500 text-slate-950' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
    }`;

  return (
    <>
      <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4">
          {/* Hero */}
          <section className="pt-2 pb-6 px-4 sm:px-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
              ADMIN · Kassenprüfer
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              Kassen-<br /><span className="text-amber-600 dark:text-amber-400">Wächter.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              {groupedAssignments.length > 0 ? (
                <>
                  <span className="font-bold text-gray-900 dark:text-white">{groupedAssignments.length} aktive Prüfer</span> ·{' '}
                  <span className="font-bold text-amber-700 dark:text-amber-300">{assignments.length} Zuweisungen</span>{' '}
                  über {new Set(assignments.map(a => a.audit_year)).size} Prüfjahre.
                </>
              ) : (
                'Vier-Augen-Prinzip im Verein — benenne Kassenprüfer, weise Jahre zu, kontrolliere Fortschritt.'
              )}
            </p>
          </section>

          <div className="px-4 sm:px-6">
            {/* Tabs */}
            <div className="mb-6 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
              <div className="inline-flex p-1 rounded-2xl whitespace-nowrap bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10">
                <button onClick={() => setActiveTab('auditors')} className={tabBtn(activeTab === 'auditors')}>
                  <UserIcon className="h-5 w-5" />
                  Kassenprüfer
                </button>
                <button onClick={() => setActiveTab('progress')} className={tabBtn(activeTab === 'progress')}>
                  <ChartBarIcon className="h-5 w-5" />
                  Prüffortschritt
                </button>
                <button onClick={() => setActiveTab('info')} className={tabBtn(activeTab === 'info')}>
                  <BookOpenIcon className="h-5 w-5" />
                  Regeln & Recht
                </button>
              </div>
            </div>

            {/* Auditors Tab */}
            {activeTab === 'auditors' && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full" />
                  </div>
                ) : groupedAssignments.length === 0 ? (
                  <div className={`rounded-3xl p-12 text-center ${cardClass}`}>
                    <ShieldCheckIcon className="h-16 w-16 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
                    <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Keine Kassenprüfer benannt</h3>
                    <p className="text-gray-600 dark:text-slate-400 mb-6">Benennen Sie Kassenprüfer für die Finanzprüfung.</p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className={`${primaryBtn} px-5 py-3 inline-flex items-center gap-2`}
                    >
                      <PlusIcon className="h-5 w-5" />
                      Ersten Kassenprüfer benennen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {groupedAssignments.map((group) => (
                      <div key={group.member_id} className={`rounded-2xl overflow-hidden ${cardClass}`}>
                        <div className="px-5 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3 border-b border-gray-200 dark:border-slate-800">
                          <div className="flex items-center gap-4">
                            <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{group.vorname} {group.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-slate-400">{group.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveAuditor(group.member_id)}
                            className="px-3 py-1.5 text-sm bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 rounded-lg transition font-bold"
                          >
                            Rolle entfernen
                          </button>
                        </div>

                        <div className="divide-y divide-gray-200 dark:divide-slate-800">
                          {group.assignments.map((assignment) => (
                            <div key={assignment.id} className="px-5 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                              <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <CalendarIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                                  <span className="font-bold text-gray-900 dark:text-white">Jahr {assignment.audit_year}</span>
                                </div>

                                {assignment.status === 'completed' ? (
                                  <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm font-bold">
                                    <LockClosedIcon className="h-4 w-4" />
                                    Abgeschlossen
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 px-2.5 py-1 bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-bold">
                                    <LockOpenIcon className="h-4 w-4" />
                                    Aktiv
                                  </span>
                                )}

                                <div className="text-sm text-gray-600 dark:text-slate-400">
                                  Zugriff: {formatDate(assignment.access_start)} - {formatDate(assignment.access_end)}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {assignment.status === 'completed' && (
                                  <>
                                    <button
                                      onClick={() => handleDownloadPDF(assignment.audit_year, assignment.member_id, `${assignment.vorname}_${assignment.name}`)}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 rounded-lg transition font-bold"
                                    >
                                      <DocumentArrowDownIcon className="h-4 w-4" />
                                      PDF
                                    </button>
                                    <button
                                      onClick={() => handleReopen(assignment.id)}
                                      className="px-3 py-1.5 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 rounded-lg transition font-bold"
                                    >
                                      Wieder öffnen
                                    </button>
                                  </>
                                )}

                                {deleteConfirm === assignment.id ? (
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-rose-700 dark:text-rose-300 bg-rose-500/15 border border-rose-500/30 hover:bg-rose-500/25 rounded-lg">
                                      <CheckIcon className="h-5 w-5" />
                                    </button>
                                    <button onClick={() => setDeleteConfirm(null)} className="p-1.5 text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg">
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => setDeleteConfirm(assignment.id)} className="p-2 text-gray-600 dark:text-slate-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition">
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Progress Tab */}
            {activeTab === 'progress' && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-gray-700 dark:text-slate-300 font-bold">Jahr:</span>
                  <div className="flex gap-2 flex-wrap">
                    {availableYears.map((year) => (
                      <button
                        key={year}
                        onClick={() => setProgressYear(year)}
                        className={`px-4 py-2 rounded-xl font-bold transition ${
                          progressYear === year
                            ? 'bg-amber-500 text-slate-950'
                            : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>

                {progressLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <ArrowPathIcon className="h-8 w-8 text-amber-600 dark:text-amber-400 animate-spin" />
                  </div>
                ) : progressStats ? (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className={`rounded-2xl p-5 ${cardClass}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                            <ChartBarIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                          </div>
                          <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Buchungen</span>
                        </div>
                        <p className="text-3xl font-black text-gray-900 dark:text-white tabular-nums">{progressStats.total_bookings}</p>
                      </div>
                      <div className={`rounded-2xl p-5 ${cardClass}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                            <CheckCircleIcon className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
                          </div>
                          <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Geprüft</span>
                        </div>
                        <p className="text-3xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums">{progressStats.checked}</p>
                      </div>
                      <div className={`rounded-2xl p-5 ${cardClass}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                            <FlagIcon className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                          </div>
                          <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Beanstandet</span>
                        </div>
                        <p className="text-3xl font-black text-amber-700 dark:text-amber-300 tabular-nums">{progressStats.flagged}</p>
                      </div>
                      <div className={`rounded-2xl p-5 ${cardClass}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center">
                            <ClockIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                          </div>
                          <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Offen</span>
                        </div>
                        <p className="text-3xl font-black text-gray-700 dark:text-slate-300 tabular-nums">{progressStats.open}</p>
                      </div>
                    </div>

                    <div className={`rounded-2xl p-5 ${cardClass}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-base font-bold text-gray-900 dark:text-white">Gesamtfortschritt</span>
                        <span className="text-base font-black text-amber-600 dark:text-amber-400 tabular-nums">{progressStats.progress_percent}%</span>
                      </div>
                      <div className="w-full rounded-full h-3 overflow-hidden bg-gray-100 dark:bg-white/[0.04]">
                        <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${progressStats.progress_percent}%`, background: 'linear-gradient(90deg, rgba(245,158,11,0.5) 0%, rgba(245,158,11,0.3) 100%)', borderRight: '1px solid rgba(245,158,11,0.6)' }} />
                      </div>
                    </div>

                    {auditorProgress.length > 0 && (
                      <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800">
                          <h3 className="font-bold text-gray-900 dark:text-white">Kassenprüfer Status</h3>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-slate-800">
                          {auditorProgress.map((auditor) => (
                            <div key={auditor.id} className="px-6 py-4 flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-4">
                                <UserIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                                <span className="font-bold text-gray-900 dark:text-white">{auditor.vorname} {auditor.name}</span>
                              </div>
                              {auditor.status === 'completed' ? (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm font-bold">
                                  <CheckCircleIcon className="h-4 w-4" />
                                  Abgeschlossen {auditor.completed_at && `am ${formatDate(auditor.completed_at)}`}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-bold">
                                  <ArrowPathIcon className="h-4 w-4" />
                                  In Bearbeitung
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {flaggedBookings.length > 0 && (
                      <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800">
                          <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <FlagIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            Beanstandungen & Bemerkungen
                          </h3>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-slate-800">
                          {flaggedBookings.map((booking) => (
                            <div key={booking.id} className="px-6 py-4">
                              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                                <div>
                                  <p className="font-bold text-gray-900 dark:text-white">
                                    {booking.verwendungszweck || booking.name_zahlungsbeteiligter || 'Unbekannt'}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-slate-400 tabular-nums">
                                    {formatDate(booking.buchungstag)} • {formatCurrency(booking.betrag)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2.5 py-1 rounded text-xs font-bold ${
                                    booking.status === 'flagged'
                                      ? 'bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300'
                                      : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                                  }`}>
                                    {booking.status === 'flagged' ? 'Beanstandet' : 'Geprüft'}
                                  </span>
                                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                                    von <span className="font-bold text-gray-800 dark:text-slate-200">{booking.checker_vorname} {booking.checker_name}</span>
                                  </p>
                                </div>
                              </div>
                              {booking.note && (
                                <div className={`rounded-xl p-3 ${booking.status === 'flagged' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                                  <p className={`text-sm ${booking.status === 'flagged' ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'}`}>
                                    <strong>Bemerkung:</strong> {booking.note}
                                  </p>
                                  <p className={`text-xs mt-1 ${booking.status === 'flagged' ? 'text-amber-700/80 dark:text-amber-300/80' : 'text-blue-700/80 dark:text-blue-300/80'}`}>
                                    {formatDate(booking.checked_at)}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {flaggedBookings.length === 0 && (
                      <div className={`rounded-2xl p-8 text-center ${cardClass}`}>
                        <CheckCircleIcon className="h-12 w-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-slate-400">Keine Beanstandungen oder Bemerkungen für {progressYear}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`rounded-2xl p-8 text-center ${cardClass}`}>
                    <ChartBarIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-slate-400">Keine Daten für {progressYear} verfügbar</p>
                  </div>
                )}
              </div>
            )}

            {/* Info Tab */}
            {activeTab === 'info' && (
              <div className="space-y-6">
                {/* Gesetzliche Grundlagen */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <ScaleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      Gesetzliche Grundlagen
                    </h3>
                  </div>
                  <div className="p-6 space-y-4 text-sm text-gray-700 dark:text-slate-300">
                    <p>
                      Der Kassenprüfer ist <strong className="text-gray-900 dark:text-white">gesetzlich nicht vorgeschrieben</strong>, jedoch eine wichtige Kontrollinstanz
                      in jedem Verein. Das BGB enthält keine spezifischen Vorgaben für die Kassenprüfung – diese wird durch
                      die <strong className="text-gray-900 dark:text-white">Satzung des Vereins</strong> festgelegt.
                    </p>
                    <div className={`rounded-xl p-4 ${subTileClass}`}>
                      <p className="font-bold text-gray-900 dark:text-white mb-2">Relevante Gesetze:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li><strong className="text-gray-900 dark:text-white">Art. 9 GG</strong> – Vereinigungsfreiheit als Grundrecht</li>
                        <li><strong className="text-gray-900 dark:text-white">§§ 21-79 BGB</strong> – Wesentliche Vorschriften zum Vereinsrecht</li>
                        <li><strong className="text-gray-900 dark:text-white">§§ 27(3), 666 BGB</strong> – Pflicht zur Rechenschaftslegung</li>
                        <li><strong className="text-gray-900 dark:text-white">§ 259 BGB</strong> – Aufbewahrung von Belegen</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Rechte */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-emerald-50 dark:bg-emerald-500/[0.06]">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      Rechte der Kassenprüfer
                    </h3>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-sm text-gray-700 dark:text-slate-300">
                      {[
                        <><strong className="text-gray-900 dark:text-white">Vollständiges Einsichtsrecht</strong> in alle Geschäftsunterlagen des Vereins</>,
                        <><strong className="text-gray-900 dark:text-white">Auskunfts- und Informationsrecht</strong> gegenüber allen Vereinsorganen</>,
                        <>Zugang zu Buchungsbelegen, Kontoauszügen, Kassenbuch und allen relevanten Dokumenten</>,
                        <>Der Vorstand hat <strong className="text-gray-900 dark:text-white">kein Schweigerecht</strong> gegenüber den Kassenprüfern</>,
                      ].map((c, i) => (
                        <li key={i} className="flex gap-3">
                          <CheckIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Pflichten */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-amber-50 dark:bg-amber-500/[0.06]">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      Pflichten der Kassenprüfer
                    </h3>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 text-sm text-gray-700 dark:text-slate-300">
                      {[
                        <><strong className="text-gray-900 dark:text-white">Meldepflicht:</strong> Alle Mängel und Unregelmäßigkeiten müssen der Mitgliederversammlung mitgeteilt werden</>,
                        <><strong className="text-gray-900 dark:text-white">Verschweigen verboten:</strong> Missstände dürfen niemals im vermeintlichen Vereinsinteresse verschwiegen werden</>,
                        <><strong className="text-gray-900 dark:text-white">Ausweitung der Prüfung:</strong> Bei erheblichen Fehlern muss die Stichprobenprüfung zur vollständigen Prüfung ausgeweitet werden</>,
                        <><strong className="text-gray-900 dark:text-white">Unabhängige Prüfung:</strong> Kassenprüfer sollen unabhängig voneinander prüfen</>,
                      ].map((c, i) => (
                        <li key={i} className="flex gap-3">
                          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Wer kann Kassenprüfer werden */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-blue-50 dark:bg-blue-500/[0.06]">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                      Wer kann Kassenprüfer werden?
                    </h3>
                  </div>
                  <div className="p-6 text-sm text-gray-700 dark:text-slate-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-xl p-4" style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                        <p className="font-bold text-emerald-800 dark:text-emerald-200 mb-2">Geeignet:</p>
                        <ul className="list-disc list-inside space-y-1 text-emerald-800/90 dark:text-emerald-200/90">
                          <li>Vereinsmitglieder</li>
                          <li>Auch Nichtmitglieder möglich</li>
                          <li>Externe Experten (Steuerberater, Buchhalter)</li>
                          <li>Neutrale, unbefangene Personen</li>
                        </ul>
                      </div>
                      <div className="rounded-xl p-4" style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}>
                        <p className="font-bold text-rose-800 dark:text-rose-200 mb-2">Nicht geeignet:</p>
                        <ul className="list-disc list-inside space-y-1 text-rose-800/90 dark:text-rose-200/90">
                          <li>Vorstandsmitglieder</li>
                          <li>Personen mit leitender Position</li>
                          <li>Enge Verwandte von Vorstandsmitgliedern</li>
                          <li>Befangene Personen</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checkliste */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-amber-50 dark:bg-amber-500/[0.06]">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <ClipboardDocumentCheckIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      Checkliste für die Kassenprüfung
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <ul className="space-y-2 text-gray-700 dark:text-slate-300">
                        {['Wurde der Haushaltsplan eingehalten?',
                          'Sind alle Belege vorhanden?',
                          'Stimmen Buchungen mit Kontoauszügen überein?',
                          'Wurden Vergleichsangebote bei Anschaffungen eingeholt?',
                          'Ist das Kassenbuch vollständig?'].map((c) => (
                          <li key={c} className="flex gap-2">
                            <span className="text-amber-600 dark:text-amber-400 font-black">☐</span>
                            {c}
                          </li>
                        ))}
                      </ul>
                      <ul className="space-y-2 text-gray-700 dark:text-slate-300">
                        {['Wurden alle Konten berücksichtigt?',
                          'Sind Mitgliedsbeiträge korrekt erfasst?',
                          'Teilnehmerlisten bei Bewirtungen vorhanden?',
                          'Aufwandsentschädigungen satzungskonform?',
                          'Satzungsgemäße Verwendung der Mittel?'].map((c) => (
                          <li key={c} className="flex gap-2">
                            <span className="text-amber-600 dark:text-amber-400 font-black">☐</span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Haftung */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                      Haftung der Kassenprüfer
                    </h3>
                  </div>
                  <div className="p-6 text-sm text-gray-700 dark:text-slate-300">
                    <p className="mb-4">
                      Kassenprüfer haften gegenüber dem Verein nur in <strong className="text-gray-900 dark:text-white">absoluten Ausnahmefällen</strong>.
                      Gewöhnliche Prüfungen bergen kein Haftungsrisiko.
                    </p>
                    <div className="rounded-xl p-4" style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}>
                      <p className="font-bold text-rose-800 dark:text-rose-200 mb-2">Haftung nur bei:</p>
                      <ul className="list-disc list-inside space-y-1 text-rose-800/90 dark:text-rose-200/90">
                        <li>Grober Fahrlässigkeit</li>
                        <li>Vorsätzlicher Verschleierung von Unregelmäßigkeiten</li>
                        <li>Unterschlagung von Geldern</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Bedeutung Bericht */}
                <div className={`rounded-2xl overflow-hidden ${cardClass}`}>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 bg-amber-50 dark:bg-amber-500/[0.06]">
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <BookOpenIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      Bedeutung des Kassenprüfungsberichts
                    </h3>
                  </div>
                  <div className="p-6 text-sm text-gray-700 dark:text-slate-300">
                    <p>
                      Der Kassenprüfungsbericht ist <strong className="text-gray-900 dark:text-white">Grundlage für die Entlastung</strong> des Vorstands durch die
                      Mitgliederversammlung. Durch die Entlastung:
                    </p>
                    <ul className="list-disc list-inside mt-3 space-y-1">
                      <li>Spricht die Mitgliederversammlung dem Vorstand das Vertrauen aus</li>
                      <li>Verzichtet der Verein auf nachträgliche Schadensersatzforderungen</li>
                      <li>Wird die ordnungsgemäße Kassenführung bestätigt</li>
                    </ul>
                  </div>
                </div>

                <div className="text-xs text-gray-500 dark:text-slate-500 text-center">
                  Quellen:
                  <a href="https://www.vereinswelt.de/finanzen/kassenpruefung/kassenpruefer/" target="_blank" rel="noopener noreferrer" className="text-amber-700 dark:text-amber-300 hover:underline ml-1">vereinswelt.de</a>,
                  <a href="https://www.campai.com/de/akademie/kassenprufung-im-verein" target="_blank" rel="noopener noreferrer" className="text-amber-700 dark:text-amber-300 hover:underline ml-1">campai.com</a>,
                  <a href="https://www.meinverein.de/blog/vereinsbuchhaltung-finanzierung/kassenpruefung-im-verein/" target="_blank" rel="noopener noreferrer" className="text-amber-700 dark:text-amber-300 hover:underline ml-1">meinverein.de</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => { setShowAddModal(false); resetForm(); }}
        >
          <div
            className={`rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg flex flex-col overflow-hidden animate-slideUp ${cardClass}`}
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-gray-200 dark:border-slate-800">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-600/80 dark:text-amber-400/80 font-bold">Kassenprüfer</div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Prüfer benennen</h2>
              </div>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Mitglied auswählen *</span>
                <select
                  value={selectedMember || ''}
                  onChange={(e) => setSelectedMember(parseInt(e.target.value) || null)}
                  className={`mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                  required
                >
                  <option value="">-- Mitglied wählen --</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.vorname} {m.name} ({m.email}) {m.is_auditor ? '- bereits Kassenprüfer' : ''}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Prüfungsjahr(e) *</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {availableYears.map((year) => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => toggleYear(year)}
                      className={`px-4 py-2 rounded-xl font-bold transition ${
                        selectedYears.includes(year)
                          ? 'bg-amber-500 text-slate-950'
                          : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Zugriff ab *</span>
                  <input
                    type="date"
                    value={accessStart}
                    onChange={(e) => setAccessStart(e.target.value)}
                    className={`mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Zugriff bis *</span>
                  <input
                    type="date"
                    value={accessEnd}
                    onChange={(e) => setAccessEnd(e.target.value)}
                    className={`mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                    required
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Bemerkungen (optional)</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="z.B. Beschluss der Mitgliederversammlung vom..."
                  className={`mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none placeholder:text-gray-500 dark:placeholder:text-slate-500 placeholder:font-normal ${inputClass}`}
                />
              </label>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className={`${secondaryBtn} flex-1 px-4 py-2.5`}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={submitting || !selectedMember || selectedYears.length === 0}
                  className={`${primaryBtn} flex-1 px-4 py-2.5`}
                >
                  {submitting ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAuditors;
